'use client';

import type { TemporarySignupAction } from '@/features/marketing/lib/marketing-constants';

import './marketing-pricing.css';

type MarketingPricingSectionProps = {
  /** CTA under the plan cards (plain /login?tab=signup link). */
  ctaLabel: string;
  ctaHref?: string;
  /** When set with onCtaClick, renders a button that runs temp-signup instead of a link. */
  ctaAction?: TemporarySignupAction;
  onCtaClick?: () => void;
};

/**
 * Shared marketing pricing shell used by home, europass, and future landings.
 * DOM ids/classes must stay stable for usePublicPricing + changePlanDuration.
 */
export function MarketingPricingSection({
  ctaLabel,
  ctaHref = '/login?tab=signup',
  ctaAction,
  onCtaClick,
}: MarketingPricingSectionProps) {
  return (
    <div className="container-fluid pricing-plans" id="pricing">
      <div className="row">
        <div className="col">
          <h4 className="pricing-caption text-center">
            Build for free. Pay when you download.
          </h4>
          <p className="pricing-description text-center">
            Editing and preview stay free. A clean PDF, Word file, or email needs a pass.
          </p>
        </div>
      </div>
      <div className="row">
        <div
          className="col-md-10 col-md-offset-1 free-plan"
          id="free_plan_contents"
          style={{ display: 'none' }}
        >
          <h4 className="free-plan-text">FREE</h4>
          <span className="free-plan-title" id="free_plan_title" />
          <p className="free-plan-description" id="free_plan_description" />
        </div>
      </div>
      <div className="row">
        <div className="col">
          <h2 className="text-center no-automatic-renewals">No Automatic Renewals</h2>
        </div>
      </div>
      <p className="text-center export-pricing-features">
        Premium templates, Format checks, Full ATS report, Job fit, Cover letters, Share link,
        Clean PDF, Word, or email.
      </p>
      <div className="row feature-card plan-cards export-offer-grid export-pricing">
        <div className="col export-offer-card">
          <div className="export-pricing-badge-slot" />
          <h3>2 weeks</h3>
          <p id="export-short-price" className="plan-price" />
          <p id="export-short-credits" className="export-pricing-credits" />
          <p className="export-pricing-cta">
            <span className="btn signup-btn-teal">Get 2 weeks</span>
          </p>
        </div>
        <div className="col export-offer-card export-offer-card--long">
          <div className="export-pricing-badge-slot">
            <p className="plan-highlight">Best value</p>
          </div>
          <h3>3 months</h3>
          <p id="export-long-price" className="plan-price" />
          <p id="export-long-credits" className="export-pricing-credits" />
          <p className="export-pricing-cta">
            <span className="btn signup-btn-teal">Get 3 months</span>
          </p>
        </div>
        <div id="paid-plan-pricing" hidden>
          <div className="plan-msg" id="plan-message" />
          <ul id="paid-plan-feature-list" />
        </div>
      </div>
      <p className="text-center">
        Same features. The 3-month plan lasts longer and includes more AI credits.
      </p>
      <p className="text-center">One-time payment. No automatic renewal.</p>
      <div className="row">
        <div className="col">
          <p className="text-center">
            {onCtaClick || ctaAction ? (
              <button
                type="button"
                className="btn signup-btn-teal"
                data-cta-action={ctaAction || 'import'}
                onClick={() => {
                  if (onCtaClick) {
                    onCtaClick();
                    return;
                  }
                  (
                    window as Window & {
                      createTemporaryUser?: (action?: string) => void;
                    }
                  ).createTemporaryUser?.(ctaAction || 'import');
                }}
              >
                {ctaLabel}
              </button>
            ) : (
              <a href={ctaHref} className="btn signup-btn-teal">
                {ctaLabel}
              </a>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
